// Kierki online (owner 2026-10-29: the full 100 points, 30 s for the pass): a store with the offline store's interface
// (hearts/store.ts), fed by the server's frames, so HeartsTable.tsx and the score window work unchanged. The deal, the
// exchange and a full trick are animated from the change between two states; the server waits for each of them.
import { sameCard, type HCard, type HeartsState } from '../hearts/rules';
import { COLLECT_MS, SLOW, SWAP_MS, TRICK_MS, heartsStore, setHeartsStore, type HeartsStore, type TableState } from '../hearts/store';
import { expand, seatNamesFor, clockOf, type CardEndFrame, type CardStart, type CardStateFrame, type OnlineCardGame, type OnlineDeps } from './common';
import { renamed } from './common';

/** The server's turned view as the rules' state: the others' hands as face-down stand-ins, their passes as given or
 *  not (an empty pass: the table only asks whether one was given), no pattern. */
function toState(v: Record<string, unknown>): HeartsState {
  return {
    ...(v as unknown as HeartsState),
    hands: (v.hands as unknown[]).map(expand),
    passes: (v.passes as unknown[]).map((p, k) => (k === 0 ? (p as HCard[] | null) : p ? [] : null)),
    pattern: [],
  };
}

export function createOnlineHearts(deps: OnlineDeps): OnlineCardGame & { store: HeartsStore } {
  const empty: TableState = { game: null, selected: [], seconds: 0, deal: 0, collecting: false, swap: 0, swapping: false, demo: false, names: null, startedAt: 0, endedAt: null, deadlineAt: null };
  let state: TableState = empty;
  const listeners = new Set<() => void>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let clock: ReturnType<typeof setInterval> | null = null;
  let offline: HeartsStore | null = null;
  /** A move of mine is on its way: no other until the server's answer (a slow line, a double tap). */
  let waiting = false;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const after = (ms: number, fn: () => void) => {
    const h = setTimeout(() => {
      timers.delete(h);
      fn();
    }, ms * SLOW);
    timers.add(h);
  };
  const stopTimers = () => {
    for (const h of timers) clearTimeout(h);
    timers.clear();
    if (clock !== null) clearInterval(clock);
    clock = null;
  };

  const store: HeartsStore = {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame() {
      // online the server deals
    },
    nextHand() {
      // online the next deal comes by itself
    },
    toggleSelect(card) {
      const g = state.game;
      if (!g || g.phase !== 'pass' || g.passes[0] !== null) return;
      const has = state.selected.some((c) => sameCard(c, card));
      if (has) set({ selected: state.selected.filter((c) => !sameCard(c, card)) });
      else if (state.selected.length < 3) set({ selected: [...state.selected, card] });
    },
    pass() {
      const g = state.game;
      if (!g || g.phase !== 'pass' || g.passes[0] !== null || state.selected.length !== 3 || waiting) return;
      deps.onStep?.('kierki online: pass');
      waiting = true;
      deps.send({ type: 'pass', cards: state.selected.map((c) => ({ s: c.s, r: c.r })) });
    },
    play(card) {
      const g = state.game;
      if (!g || g.phase !== 'play' || g.turn !== 0 || waiting) return;
      waiting = true;
      deps.send({ type: 'play', card: { s: card.s, r: card.r } });
    },
    stop: stopTimers,
    load() {
      // e2e only, offline
    },
    backdate() {
      // e2e only, offline
    },
  };

  return {
    game: 'hearts',
    store,
    begin(start: CardStart) {
      stopTimers();
      offline ??= heartsStore();
      state = { ...empty, deal: state.deal, swap: state.swap, names: seatNamesFor(start), ...clockOf(start) };
      setHeartsStore(store);
      clock = setInterval(() => {
        const g = state.game;
        if (g && g.phase !== 'over' && g.phase !== 'hand-end') set({ seconds: state.seconds + 1 });
      }, 1000);
    },
    state(f: CardStateFrame) {
      waiting = false;
      const g = toState(f.state);
      const before = state.game;
      const deadlineAt = f.deadline !== undefined ? Date.now() + f.deadline : null;
      if (!before || g.handNo !== before.handNo) {
        // a new deal: the score window of the last one goes, the view throws the cards out
        deps.closeWindow('hearts_score');
        set({ game: g, deal: state.deal + 1, selected: [], collecting: false, swapping: false, deadlineAt });
        return;
      }
      if (before.phase === 'pass' && g.phase === 'play') {
        // everyone passed: the exchange is shown first (the server waits for it)
        set({ game: g, selected: [], swap: state.swap + 1, swapping: true, deadlineAt });
        after(SWAP_MS, () => set({ swapping: false }));
        return;
      }
      set({ game: g, collecting: false, deadlineAt, ...(g.phase === 'pass' && g.passes[0] !== null ? { selected: [] } : {}) });
      // a full trick slides to its taker at the end of the server's wait
      if (g.phase === 'trick-done') after(TRICK_MS - COLLECT_MS, () => state.game === g && set({ collecting: true }));
      if (g.phase === 'hand-end') deps.openWindow('hearts_score');
      if (g.phase === 'over') set({ endedAt: Date.now() });
    },
    end(f: CardEndFrame) {
      const g = state.game;
      if (g && g.phase !== 'over') set({ game: { ...g, phase: 'over', ...(f.scores ? { scores: f.scores } : {}) }, endedAt: Date.now() });
      set({ deadlineAt: null });
      if (clock !== null) clearInterval(clock);
      clock = null;
      deps.openWindow('hearts_score');
    },
    rename(seat, name, bot, flag) {
      set({ names: renamed(state.names, seat, name, bot, flag) });
    },
    stop() {
      stopTimers();
      deps.closeWindow('hearts_score');
      if (offline) setHeartsStore(offline);
      state = { ...empty, deal: state.deal, swap: state.swap };
    },
  };
}

